import Container from '@embracesbs/component-container'
import React, { useContext, useEffect, useState } from 'react'
import Text from '@embracesbs/component-text'
import Icon from '@embracesbs/component-icon'
import { DeployStatus, Tenant, TenantConfig } from '../../Interfaces/Types'
import Divider from '@embracesbs/component-divider'
import Switch from '@embracesbs/component-switch'
import Checkbox from '@embracesbs/component-checkbox'
import TextField from '@embracesbs/component-textfield'
import TextFieldWrapper from '@embracesbs/component-textfieldwrapper'
import EmptyState from '@embracesbs/component-emptystate'
import Button2 from '@embracesbs/component-button2'
import Autocomplete from '@embracesbs/component-autocomplete'
import Toast, { customToast } from '@embracesbs/component-toast'
import { useTenants } from '../Context/TenantContext'
import ConnectivityChecker from './ConnectivityChecker'
import { useApi, useAuth } from '../../Hooks'
import SecretField from './SecretField'
import ClientSecretExpiryField from './ClientSecretExpiryField'
import AdditionalOidcProviders, { validateOidcProviders } from './AdditionalOidcProviders'
import Accordion from '@embracesbs/component-accordion/Accordion'
import { Language, LanguageRepository } from 'languagescore-ts'
import SectionCard from '../Common/SectionCard'
import { useSystemStatus } from '../Context/SystemStatusContext'
import { AuthRoleContext } from '../Auth/AuthRoleContext'
import { useCalmMode } from '../Context/PreferencesContext'
import { embracecloudTld } from '../../Utilities/embracecloudTld'

const enabledLanguageOptions: { label: string; value: string }[] = LanguageRepository.getEnabledLanguages().map((lang: Language) => ({
  label: lang.isoCode,
  value: lang.isoCode
}))

const defaultAvailableLanguages = ['en-GB', 'nl-NL']

// A bare hostname: dot-separated labels, no scheme and no path. The Terraform side builds
// "https://<hostname>/*" out of this for the realm redirect URLs, so a pasted URL would
// produce a broken one.
const HOSTNAME_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i

// Looser than the additional-provider alias rule: this must match an alias someone may
// have typed by hand in the Keycloak console, while staying safe in kc_idp_hint and broker URLs.
const EMPLOYEES_ALIAS_PATTERN = /^[A-Za-z0-9._-]+$/

// Keycloak's internal client id, which is what an import needs, rather than the human-readable
// clientId. Always a UUID.
const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

// Environments allowed to enable the housing realm. A rollout decision rather than a structural
// limit: there is a housing Keycloak per target (housing.auth.alpha for nonproduction,
// housing.auth.beta for beta, housing.auth for production), so any environment could be added
// once it is ready.
//
// Adding an environment here is half the change. The Terraform gate lives in
// devops/terraform/providers-tenants.tf (local.housing_keycloak_environments) and is passed down
// to _modules_tenants/_main, so widening the feature means editing this list and that one.
const HOUSING_KEYCLOAK_ENVIRONMENTS = ['alpha', 'beta', 'production']

// The realm's Frontend URL is only configurable on alpha for now. Hiding the group rather than
// disabling the field: an empty hostname is a valid configuration, so a greyed-out empty box
// would read as a setting waiting to be filled rather than one that does not apply.
const HOUSING_HOSTNAME_ENVIRONMENTS = ['alpha']

// Webcenter identity hosts follow a fixed convention, so the housing employees OIDC hostname can
// be filled in for the operator when they switch the provider on. Only ever a starting value: the
// field stays editable because a tenant on a custom host has to be able to override it.
const webcenterHostnameFor = (tenant?: Tenant): string =>
  tenant ? `${tenant.tenantName}.webcenteridentity.${tenant.environment}.embracecloud.${embracecloudTld(tenant.environment)}` : ''

interface TenantDeploymentSettingsProps { }

const TenantDeploymentSettings: React.FC<TenantDeploymentSettingsProps> = ({ }) => {
  const { fetchApi } = useApi()
  const { isLoggedIn } = useAuth()
  const [tenantConfig, setTenantConfig] = useState<TenantConfig | undefined>(undefined)
  const [tenantConfigIsLoading, setTenantConfigIsLoading] = useState(true)
  const { selectedTenantId, selectedTenant, setTenantDeployStatus, revertTenantDeployStatus } = useTenants()
  const { maintenance, reservedEnvironments, scheduledEnvironments } = useSystemStatus()
  const roles = useContext(AuthRoleContext)
  // Calm (readability) preference: adds air to this dense form via the
  // .calm-form CSS rules in index.css.
  const isCalm = useCalmMode()
  // During maintenance mode only admins may run updates.
  const isMaintenanceBlocked = maintenance.enabled && !roles?.isAdmin
  // A stage with a scheduled/active rollout is reserved: manual updates are
  // blocked for everyone (including admins), the rollout drives them instead.
  // A scheduled (not yet running) rollout is called out separately.
  const isRolloutReserved = reservedEnvironments.some((env) => env.toLowerCase() === (selectedTenant?.environment ?? '').toLowerCase())
  const isRolloutScheduled = scheduledEnvironments.some((env) => env.toLowerCase() === (selectedTenant?.environment ?? '').toLowerCase())
  const updateBlockedReason = isRolloutScheduled
    ? `Rollout scheduled: updates disabled`
    : isRolloutReserved
      ? `Stage is in a rollout: updates disabled`
      : isMaintenanceBlocked
        ? `Maintenance mode: updates disabled`
        : null
  const isUpdateBlocked = updateBlockedReason !== null
  // 'plan' queues a plan-only run of the saved configuration (WI 165448): the fields are read-only.
  const [updateMode, setUpdateMode] = useState<'full' | 'suite' | 'plan'>('full')
  // The configuration as last loaded or saved. Plan mode shows it, since that is what a plan
  // runs; the unsaved edits are set aside meanwhile and come back on leaving plan mode.
  const [savedConfig, setSavedConfig] = useState<TenantConfig | undefined>(undefined)
  const [editsBeforePlan, setEditsBeforePlan] = useState<{ config: TenantConfig | undefined; displayName: string } | null>(null)
  // Testing the SQL configuration read (WI 165448): an admin chooses where the pipeline reads the
  // tenant configuration, '' leaves the pipeline default. The API refuses the choice from others.
  const [configSource, setConfigSource] = useState<'' | 'sql' | 'vault'>('')
  const [planRun, setPlanRun] = useState<{ tenantId: string; runId: number; runUrl: string } | null>(null)
  const [isPlanQueuing, setIsPlanQueuing] = useState(false)
  const [displayName, setDisplayName] = useState<string>('')
  const [selectedPortalUIMediaHandlerFileTypes, setSelectedPortalUIMediaHandlerFileTypes] = useState<{ label: string; value: string }[]>([])
  const [selectedAvailableLanguages, setSelectedAvailableLanguages] = useState<{ label: string; value: string }[]>([])

  // Backs "Use Hive-gateway 2 (GQL)" / "Use Gateway API (GQL)". There is no equivalent toggle for
  // WS: it always goes through the Gateway API, so both WS switches are permanently on.
  const isHiveGatewayAlwaysOn = ['alpha', 'stable-alpha'].includes(selectedTenant?.environment ?? '')
  // Effective state of the hive gateway: the Gateway API rides on top of it and follows this flag.
  const isHiveGatewayOn = isHiveGatewayAlwaysOn || (tenantConfig?.useHiveGateway ?? false)

  // Mirrors the hostname construction in devops/terraform/_modules_tenants/_main/main.tf, purely
  // to show operators which hostname each gateway toggle points at.
  const gatewayEnvironment = selectedTenant?.environment ?? ''
  const gatewayTld = embracecloudTld(gatewayEnvironment)
  const hiveGateway2Hostname =
    gatewayEnvironment === 'production' ? 'hive-gateway.embracecloud.nl' : `hive-gateway.${gatewayEnvironment}.embracecloud.${gatewayTld}`
  const gatewayApiHostname =
    gatewayEnvironment === 'production' ? 'gateway.embracecloud.nl' : `gateway.${gatewayEnvironment}.embracecloud.${gatewayTld}`

  const isAdfsAuth = tenantConfig?.authenticationType === 'Adfs'
  const isFederatedAuth = isAdfsAuth || tenantConfig?.authenticationType === 'AzureAd'
  const idpIdentifierField = isAdfsAuth ? 'idpAzureHostname' : 'idpAzureAdTenantId'
  // Entra's multi-tenant authorities, accepted in place of a tenant ID (identity-provider-modules 0.26+).
  const isMultiTenantAuthority =
    !isAdfsAuth && ['organizations', 'common', 'consumers'].includes(tenantConfig?.idpAzureAdTenantId?.trim().toLowerCase() ?? '')

  useEffect(() => {
    const abortController = new AbortController()
    const signal = abortController.signal

    function fetchTenantConfig() {
      if (!isLoggedIn || selectedTenantId === null) {
        setTenantConfigIsLoading(false)
        return
      }

      setTenantConfigIsLoading(true)
      setTenantConfig(undefined)

      console.log('Fetching tenant config for', selectedTenantId)

      fetchApi({
        endpoint: `/api/tenant/${selectedTenantId}/tenantsconfiguration`,
        method: 'GET',
        signal
      })
        .then((response) => {
          if (!signal.aborted) {
            const normalized = {
              ...response,
              feLanguage: LanguageRepository.getLanguageByNormalizedIsoCode(response.feLanguage)?.isoCode || response.feLanguage,
              feAvailableLanguages: (response.feAvailableLanguages || '')
                .split(',')
                .map((c: string) => LanguageRepository.getLanguageByNormalizedIsoCode(c.trim())?.isoCode || c.trim())
                .filter((c: string) => c !== '')
                .join(',')
            }
            setTenantConfig(normalized)
            setSavedConfig(normalized)
            console.log('Finished fetching tenant config for', selectedTenantId)
          }
        })
        .catch((error) => {
          if (!signal.aborted) {
            console.error('Failed to fetch tenant config', error)
          }
        })
        .finally(() => {
          if (!signal.aborted) {
            setTenantConfigIsLoading(false)
          }
        })
    }

    if (selectedTenantId !== null) {
      fetchTenantConfig()
    } else {
      setTenantConfig(undefined)
      setTenantConfigIsLoading(false)
    }

    return () => {
      console.log('Aborting any in-flight tenant config requests')
      abortController.abort()
    }
  }, [isLoggedIn, selectedTenantId])

  // Another tenant starts in tenant-update mode with the pipeline's default source, so an admin's
  // choice for one tenant never carries over to the next one's deploy.
  useEffect(() => {
    setUpdateMode('full')
    setConfigSource('')
    setEditsBeforePlan(null)
  }, [selectedTenantId])

  useEffect(() => {
    if (selectedTenant) {
      setDisplayName(selectedTenant.tenantDisplayname || '')
    }
  }, [selectedTenant])

  // Sync selectedPortalUIMediaHandlerFileTypes with tenantConfig
  useEffect(() => {
    if (tenantConfig?.portalUIMediaHandlerAcceptedFileTypes) {
      const fileTypes = tenantConfig.portalUIMediaHandlerAcceptedFileTypes.split(',').map((type: string) => ({
        label: type.trim(),
        value: type.trim()
      }))
      setSelectedPortalUIMediaHandlerFileTypes(fileTypes)
    } else {
      setSelectedPortalUIMediaHandlerFileTypes([])
    }
  }, [tenantConfig?.portalUIMediaHandlerAcceptedFileTypes])

  // Sync selectedAvailableLanguages with tenantConfig
  useEffect(() => {
    const raw = tenantConfig?.feAvailableLanguages
    const isoCodes = raw && raw.trim() !== '' ? raw.split(',').map((c) => c.trim()) : defaultAvailableLanguages
    setSelectedAvailableLanguages(
      isoCodes
        .map((code) => enabledLanguageOptions.find((opt) => opt.value === code))
        .filter((opt): opt is { label: string; value: string } => opt !== undefined)
    )
  }, [tenantConfig?.feAvailableLanguages])

  const isUpdating = (state?: DeployStatus) => {
    if (state === 'updating') {
      return true
    }

    return false
  }

  const handleTenantConfigurationChange = (name: string, value: any) => {
    setTenantConfig((prevConfig) => {
      // Return early if prevConfig is undefined for some reason
      if (!prevConfig) return prevConfig

      // Here we check if `name` is a key of TenantConfig to satisfy TypeScript
      if (name in prevConfig) {
        return {
          ...prevConfig,
          [name]: value // Assuming you want to store "true"/"false" strings
        } as TenantConfig // Explicitly cast the return type as TenantConfig
      }
      return prevConfig
    })
  }

  // Switching the hive gateway off also switches the Gateway API off: it cannot run without it.
  const handleUseHiveGatewayChange = (useHiveGateway: boolean) => {
    setTenantConfig((prevConfig) =>
      prevConfig
        ? {
          ...prevConfig,
          useHiveGateway,
          enableGatewayApi: useHiveGateway ? prevConfig.enableGatewayApi : false
        }
        : prevConfig
    )
  }

  // The reverse of the above: enabling the Gateway API also switches the hive gateway on, since it
  // rides on top of it. Disabling the Gateway API leaves the hive gateway as-is.
  const handleUseGatewayApiChange = (enableGatewayApi: boolean) => {
    setTenantConfig((prevConfig) =>
      prevConfig
        ? {
          ...prevConfig,
          enableGatewayApi,
          useHiveGateway: enableGatewayApi ? true : prevConfig.useHiveGateway
        }
        : prevConfig
    )
  }

  const handleUpdate = async () => {
    if (!isLoggedIn || !selectedTenantId) {
      console.error('Access token or tenant ID is null')
      return
    }

    if (isRolloutReserved) {
      customToast.error(
        isRolloutScheduled
          ? 'A rollout is scheduled for this stage: manual updates are disabled until it runs'
          : 'This stage is in a rollout: manual updates are disabled until it finishes'
      )
      return
    }
    if (isMaintenanceBlocked) {
      customToast.error('Maintenance mode is active: tenant updates are temporarily disabled')
      return
    }

    // Required-field validation. Skipped in 'suite' mode, where these general/auth
    // fields are disabled and not part of the update.
    if (updateMode !== 'suite') {
      const missing: string[] = []
      if (!tenantConfig?.feHostname?.trim()) missing.push('Suite Hostname')
      if (!selectedAvailableLanguages || selectedAvailableLanguages.length === 0) missing.push('Available Languages')
      if (!tenantConfig?.feLanguage) missing.push('Language')
      if (!tenantConfig?.authenticationType) missing.push('Authentication Type')
      if (isFederatedAuth) {
        if (!tenantConfig?.[idpIdentifierField]?.trim()) missing.push(isAdfsAuth ? 'ADFS Hostname' : 'Tenant Id')
        if (!tenantConfig?.clientId?.trim()) missing.push('Client ID')
        if (!tenantConfig?.clientSecret?.trim()) missing.push('Client Secret')
      }
      if (missing.length > 0) {
        customToast.error(`Please fill in the required fields: ${missing.join(', ')}`)
        return
      }
      const providerProblems = validateOidcProviders(tenantConfig?.idpAdditionalOidcProviders)
      if (providerProblems.length > 0) {
        customToast.error(`Additional identity providers: ${providerProblems.join('; ')}`)
        return
      }
    }

    const employeesAlias = tenantConfig?.idpEmployeesAlias?.trim()
    if (employeesAlias && !EMPLOYEES_ALIAS_PATTERN.test(employeesAlias)) {
      customToast.error('Employees IdP alias may only contain letters, digits, ".", "_" and "-"')
      return
    }

    // Optimistically flip the status to 'updating' so the list, buttons and
    // properties react instantly; rolled back below if the request fails.
    setTenantDeployStatus(selectedTenantId, 'updating')

    const params = new URLSearchParams({ updateMode })
    if (displayName.trim()) {
      params.append('displayname', displayName.trim())
    }
    if (roles?.isAdmin && configSource) {
      params.append('configSource', configSource)
    }

    fetchApi({
      endpoint: `/api/tenant/${selectedTenantId}/tenantsconfiguration?${params.toString()}`,
      method: 'PUT',
      body: tenantConfig
    })
      .then(() => {
        setSavedConfig(tenantConfig)
        customToast.success(`${updateMode} update accepted`)
      })
      .catch((error) => {
        revertTenantDeployStatus(selectedTenantId)
        customToast.error('Failed to start an update')
        console.error(error)
      })
  }

  // A plan-only run: terraform plan in every stage, and nothing is saved or changed. It plans the
  // saved configuration, so unsaved edits on this page are not part of it.
  const handlePlan = () => {
    if (!isLoggedIn || !selectedTenantId) return
    const tenantId = selectedTenantId
    const params = new URLSearchParams({ updateMode: 'full' })
    if (roles?.isAdmin && configSource) {
      params.append('configSource', configSource)
    }

    setIsPlanQueuing(true)
    fetchApi({ endpoint: `/api/tenant/${tenantId}/plan?${params.toString()}`, method: 'POST' })
      .then((response) => {
        if (response?.runUrl) {
          setPlanRun({ tenantId, runId: response.runId, runUrl: response.runUrl })
          customToast.success(`Plan run ${response.runId} queued`)
        }
      })
      .catch((error) => {
        customToast.error(
          error?.status === 423
            ? 'An update is running: plan once it has finished'
            : typeof error?.data === 'string' && error.data
              ? error.data
              : 'Failed to queue a plan run'
        )
        console.error(error)
      })
      .finally(() => setIsPlanQueuing(false))
  }

  const housingKeycloakAvailable = HOUSING_KEYCLOAK_ENVIRONMENTS.includes(selectedTenant?.environment ?? '')
  const housingHostnameAvailable = HOUSING_HOSTNAME_ENVIRONMENTS.includes(selectedTenant?.environment ?? '')

  // Shows the operator the redirect URL the Umbraco client will actually get. Mirrors
  // local.umbraco_redirect_urls in _modules_tenants/_main/main.tf: strip a stray scheme, trim
  // slashes, and treat an empty Portal hostname as no redirect URL rather than "https:///…".
  // Keep the two in step — a mismatch here is a login failure the operator cannot see coming.
  const umbracoPortalHost = (tenantConfig?.fePortalUrl ?? '')
    .trim()
    .replace('https://', '')
    .replace(/^\/+|\/+$/g, '')
  const umbracoRedirectUrl = umbracoPortalHost ? `https://${umbracoPortalHost}/umbraco/` : ''

  // Terraform turns the client on for every tenant that has customers, so the switch only still
  // decides anything on NoCustomers.
  const umbracoClientForcedOn = tenantConfig?.feRunMode !== 'NoCustomers'

  const isFieldEnabled = (fieldName: keyof TenantConfig) => {
    if (isUpdating(selectedTenant?.deployStatus)) return false

    // A plan runs the saved configuration, so nothing on the page can be edited for it.
    if (updateMode === 'plan') return false

    if (updateMode === 'suite') {
      const allowedFields = [
        'suiteVersion',
        'gateway1CustomerAplicationId',
        'gateway1CustomerHostname',
        'customersUIHostname',
        'customersBackendPortForward',
        'customersMailEnabled',
        'customersChatEnabled',
        'customersTelephonyEnabled',
        'customersTasksEnabled',
        'customersCaseflowEnabled',
        'customersDossiersEnabled',
        'customersPhoneHealthCheckEnabled',
        'customersDisableChatNotifications',
        'queryConfigFromGraph',
        'matomoInteralHostname',
        'matomoInteralSiteId',
        'matomoInternalContainerId',
        'liquitUrl',
        'logRocketApiKey',
        'googleAnalyticsId',
        'idpDomainHint',
        'idpLoginPrompt',
        'clientwebIpRanges',
        'idpEmployeesAlias',
        'useHiveSubscriptions',
        'useHiveGateway',
        'enableGatewayApi',
        'publicPortalThemingEnabled'
      ]

      return allowedFields.includes(fieldName)
    }

    return true
  }

  // The switcher cycles tenant update -> suite update -> plan -> tenant update.
  const nextUpdateMode = ({ full: 'suite', suite: 'plan', plan: 'full' } as const)[updateMode]
  const updateModeLabels = { full: 'tenant update', suite: 'suite update', plan: 'plan (shows the changes, applies nothing)' }

  const switchUpdateMode = () => {
    if (nextUpdateMode === 'plan') {
      // Show the saved configuration, which is what the plan runs, and keep the edits aside.
      setEditsBeforePlan({ config: tenantConfig, displayName })
      setTenantConfig(savedConfig)
      setDisplayName(selectedTenant?.tenantDisplayname || '')
    } else if (updateMode === 'plan' && editsBeforePlan) {
      setTenantConfig(editsBeforePlan.config)
      setDisplayName(editsBeforePlan.displayName)
      setEditsBeforePlan(null)
    }
    setUpdateMode(nextUpdateMode)
  }

  const isPlanMode = updateMode === 'plan'

  const renderUpdateButtons = () => {
    return (
      <Container isFullWidth style={{ position: 'relative', display: 'flex', width: '100%' }}>
        <Container
          style={{
            display: 'flex',
            flexShrink: 0,
            width: '100%',
            gap: '1px'
          }}
        >
          <Button2
            style={{
              flex: 1,
              borderTopRightRadius: 0,
              borderBottomRightRadius: 0,
              flexShrink: 0
            }}
            buttonRadius='8px'
            buttonSize='30px'
            text={
              isUpdating(selectedTenant?.deployStatus)
                ? 'Updating...'
                : isPlanMode
                  ? isPlanQueuing
                    ? 'Planning...'
                    : 'Plan Tenant'
                  : updateBlockedReason
                    ? updateBlockedReason
                    : updateMode === 'suite'
                      ? 'Update Suite'
                      : 'Update Tenant'
            }
            // A plan changes nothing, so a rollout reservation or maintenance does not block it.
            isDisabled={isUpdating(selectedTenant?.deployStatus) || (isPlanMode ? isPlanQueuing : isUpdateBlocked)}
            onClick={isPlanMode ? handlePlan : handleUpdate}
            variant='contained'
            aria-label={isPlanMode ? 'Plan tenant' : 'Update tenant'}
          />
          <Button2
            style={{
              width: '38px',
              borderTopLeftRadius: 0,
              borderTopRightRadius: '8px',
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: '8px',
              flexShrink: 0
            }}
            buttonRadius='8px'
            buttonSize='30px'
            leftElement={<Icon iconName='Swap' size={14} />}
            isDisabled={isUpdating(selectedTenant?.deployStatus) || isPlanQueuing}
            onClick={switchUpdateMode}
            variant='contained'
            tooltip={`Switch to ${updateModeLabels[nextUpdateMode]}`}
            tooltipPlace='top'
            aria-label='Switch update mode'
          />
        </Container>
      </Container>
    )
  }

  const renderPlanAndSourceRow = () => {
    const showPlanLink = planRun && planRun.tenantId === selectedTenantId
    if (!roles?.isAdmin && !showPlanLink) return null

    return (
      <Container style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '8px' }}>
        {roles?.isAdmin && (
          <TextFieldWrapper labelText='Configuration source (admin)' style={{ width: '220px' }}>
            <Autocomplete
              value={configSource}
              isDisabled={isUpdating(selectedTenant?.deployStatus)}
              onChange={(e) => setConfigSource(e.value)}
              options={[
                { label: 'Pipeline default', value: '' },
                { label: 'SQL', value: 'sql' },
                { label: 'Key Vault', value: 'vault' }
              ]}
            />
          </TextFieldWrapper>
        )}
        {showPlanLink && (
          <a href={planRun.runUrl} target='_blank' rel='noopener noreferrer'>
            <Text>{`Plan run ${planRun.runId} in Azure DevOps`}</Text>
          </a>
        )}
      </Container>
    )
  }

  if (tenantConfigIsLoading) {
    return (
      // height (not flex): the tab scroll container is a block context, so
      // flex:1 collapses and the loader would hug the top instead of centering.
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <EmptyState
          animationName='searching'
          size='medium'
          title='Loading deploy configuration'
          text='Fetching the deploytime settings for this tenant…'
        />
      </div>
    )
  }

  return (
    <>
      <Toast />
      <div className={isCalm ? 'calm-form' : undefined} style={{ position: 'relative', paddingBottom: '8px' }}>
        <SectionCard title='General Settings'>
          <Container isVertical>
            <Container>
              <TextFieldWrapper labelText={'Suite Hostname'} isRequired style={{ flex: 3 }}>
                <TextField
                  value={tenantConfig?.feHostname || ''}
                  isDisabled={!isFieldEnabled('feHostname')}
                  onChange={(e) => handleTenantConfigurationChange('feHostname', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Suite Runmode'} style={{ flex: 1 }}>
                <Autocomplete
                  placeholder={'Select a value...'}
                  value={tenantConfig?.feRunMode}
                  isDisabled={!isFieldEnabled('feRunMode')}
                  onChange={(e) => handleTenantConfigurationChange('feRunMode', e.value)}
                  options={[
                    {
                      label: 'Full',
                      value: 'Full'
                    },
                    {
                      label: 'NoCustomers',
                      value: 'NoCustomers'
                    },
                    {
                      label: 'CustomersOnly',
                      value: 'CustomersOnly'
                    }
                  ]}
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Display name'} style={{ flex: 1 }}>
                <TextField
                  value={displayName}
                  isDisabled={isUpdating(selectedTenant?.deployStatus) || updateMode === 'plan'}
                  onChange={(e) => setDisplayName(e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Available Languages'} isRequired style={{ flex: 3 }}>
                <Autocomplete
                  isMulti
                  hasDropDownArrow={false}
                  options={enabledLanguageOptions}
                  value={selectedAvailableLanguages}
                  isDisabled={!isFieldEnabled('feAvailableLanguages')}
                  {...(selectedAvailableLanguages.length === 0
                    ? {
                      hasAnError: true,
                      errorMessage: 'At least one language must be selected',
                      errorMessageColor: 'danger' as const
                    }
                    : {})}
                  onChange={(selected: { label: string; value: string }[]) => {
                    if (selected.length === 0) {
                      return
                    }
                    setSelectedAvailableLanguages(selected)
                    const codes = selected.map((s) => s.value)
                    handleTenantConfigurationChange('feAvailableLanguages', codes.join(','))
                    if (tenantConfig?.feLanguage && !codes.includes(tenantConfig.feLanguage)) {
                      handleTenantConfigurationChange('feLanguage', codes[0])
                    }
                  }}
                  placeholder='Default: en-GB, nl-NL'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Language'} isRequired style={{ flex: 1 }}>
                <Autocomplete
                  value={tenantConfig?.feLanguage}
                  isDisabled={!isFieldEnabled('feLanguage')}
                  onChange={(e) => handleTenantConfigurationChange('feLanguage', e.value)}
                  options={selectedAvailableLanguages}
                />
              </TextFieldWrapper>
            </Container>
          </Container>
        </SectionCard>
        <SectionCard title='Authentication Settings'>
          <Container isVertical>
            <Container>
              <TextFieldWrapper labelText={'Authentication Type'} isRequired>
                <Autocomplete
                  value={tenantConfig?.authenticationType}
                  isDisabled={!isFieldEnabled('authenticationType')}
                  onChange={(e) => handleTenantConfigurationChange('authenticationType', e.value)}
                  options={[
                    {
                      label: 'AzureAd',
                      value: 'AzureAd'
                    },
                    {
                      label: 'ADFS',
                      value: 'Adfs'
                    },
                    {
                      label: 'Local',
                      value: 'local'
                    }
                  ]}
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={isAdfsAuth ? 'ADFS Hostname' : 'Tenant Id'} isRequired={isFederatedAuth}>
                <TextField
                  value={tenantConfig?.[idpIdentifierField]}
                  isDisabled={!isFederatedAuth || !isFieldEnabled(idpIdentifierField)}
                  onChange={(e) => handleTenantConfigurationChange(idpIdentifierField, e.target.value)}
                  type='text'
                  variant='default'
                  placeholder={isAdfsAuth ? undefined : 'Tenant GUID, or organizations / common / consumers'}
                />
                {isMultiTenantAuthority && (
                  <Text textStyle='description'>
                    Multi-tenant: users from any Entra tenant the app registration admits can sign in, and Keycloak skips the issuer check.
                  </Text>
                )}
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Client ID'} isRequired={isFederatedAuth}>
                <TextField
                  value={tenantConfig?.clientId}
                  isDisabled={!isFederatedAuth || !isFieldEnabled('clientId')}
                  onChange={(e) => handleTenantConfigurationChange('clientId', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <SecretField
                fieldName='clientSecret'
                secretName='idp-clientsecret'
                labelText='Client Secret'
                value={tenantConfig?.clientSecret || ''}
                onChange={handleTenantConfigurationChange}
                isDisabled={!isFederatedAuth || !isFieldEnabled('clientSecret')}
                tenantId={selectedTenantId}
              />
            </Container>
            {selectedTenantId && (tenantConfig?.authenticationType === 'AzureAd' || selectedTenant?.clientSecretExpiresOnUtc) && (
              <Container>
                <ClientSecretExpiryField
                  tenantId={selectedTenantId}
                  expiresOnUtc={selectedTenant?.clientSecretExpiresOnUtc ?? null}
                  clearOnly={tenantConfig?.authenticationType !== 'AzureAd'}
                />
              </Container>
            )}
            <Container>
              <SecretField
                fieldName='supportPassword'
                secretName='idp-support-password'
                labelText='Keycloak support account password'
                value={tenantConfig?.supportPassword || ''}
                onChange={handleTenantConfigurationChange}
                isDisabled={!isFieldEnabled('supportPassword')}
                tenantId={selectedTenantId}
                style={{ flex: 2 }}
                regex='^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{8,}$'
              />
              <TextFieldWrapper labelText={'Suite idp domain hint'} style={{ flex: 1, alignSelf: 'flex-start' }}>
                <TextField
                  value={tenantConfig?.idpDomainHint}
                  isDisabled={!isFieldEnabled('idpDomainHint')}
                  onChange={(e) => handleTenantConfigurationChange('idpDomainHint', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Login prompt'} style={{ flex: 1, alignSelf: 'flex-start' }}>
                <Autocomplete
                  placeholder={'Select a value...'}
                  value={tenantConfig?.idpLoginPrompt}
                  isDisabled={!isFieldEnabled('idpLoginPrompt')}
                  onChange={(e) => handleTenantConfigurationChange('idpLoginPrompt', e.value)}
                  options={[
                    {
                      label: 'unspecified',
                      value: 'unspecified'
                    },
                    {
                      label: 'select_account',
                      value: 'select_account'
                    },
                    {
                      label: 'login',
                      value: 'login'
                    }
                  ]}
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText='Auto-select the employees IdP for these IP ranges (comma-separated CIDR)' style={{ flex: 2 }}>
                <TextField
                  value={tenantConfig?.clientwebIpRanges || ''}
                  isDisabled={!isFieldEnabled('clientwebIpRanges')}
                  onChange={(e) => handleTenantConfigurationChange('clientwebIpRanges', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Login theme'} style={{ flex: 1 }}>
                <Autocomplete
                  placeholder={'Select a value...'}
                  value={tenantConfig?.idpLoginTheme}
                  isDisabled={!isFieldEnabled('idpLoginTheme')}
                  onChange={(e) => handleTenantConfigurationChange('idpLoginTheme', e.value)}
                  options={[
                    {
                      label: 'EmbraceCloud',
                      value: 'embracecloud'
                    },
                    {
                      label: 'Public Portals',
                      value: 'public_portals'
                    }
                  ]}
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Forms Password policy'} style={{ flex: 1 }}>
                <Autocomplete
                  placeholder={'Select a value...'}
                  value={tenantConfig?.idpFormsPasswordPolicy}
                  isDisabled={!isFieldEnabled('idpFormsPasswordPolicy')}
                  onChange={(e) => handleTenantConfigurationChange('idpFormsPasswordPolicy', e.value)}
                  options={[
                    {
                      label: 'Default',
                      value: 'default'
                    },
                    {
                      label: 'Strict',
                      value: 'strict'
                    }
                  ]}
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Azure AD display name'} style={{ flex: 2 }}>
                <TextField
                  isDisabled={!isFieldEnabled('idpAzureDisplayName')}
                  value={tenantConfig?.idpAzureDisplayName}
                  onChange={(e) => handleTenantConfigurationChange('idpAzureDisplayName', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Azure AD display order'} style={{ flex: selectedTenant?.environment === 'alpha' ? 1 : 2 }}>
                <TextField
                  isDisabled={!isFieldEnabled('idpAzureDisplayOrder')}
                  value={tenantConfig?.idpAzureDisplayOrder}
                  onChange={(e) => handleTenantConfigurationChange('idpAzureDisplayOrder', e.target.value)}
                  type='number'
                  variant='default'
                  placeholder='Lower number appears first'
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Public portal theme id'}>
                <TextField
                  isDisabled={!isFieldEnabled('idpPublicPortalThemeId')}
                  value={tenantConfig?.idpPublicPortalThemeId}
                  onChange={(e) => handleTenantConfigurationChange('idpPublicPortalThemeId', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Employees IdP alias (skip login page and IP ranges)'}>
                <TextField
                  isDisabled={!isFieldEnabled('idpEmployeesAlias')}
                  value={tenantConfig?.idpEmployeesAlias ?? ''}
                  onChange={(e) => handleTenantConfigurationChange('idpEmployeesAlias', e.target.value)}
                  type='text'
                  variant='default'
                  placeholder='employees'
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <Checkbox
                name='employeesLogin'
                label='Skip keycloak login page'
                description='Default to the employees IdP alias'
                isChecked={tenantConfig?.idpSkipKeycloakLoginPage}
                isDisabled={!isFieldEnabled('idpSkipKeycloakLoginPage')}
                onClick={() => handleTenantConfigurationChange('idpSkipKeycloakLoginPage', !tenantConfig?.idpSkipKeycloakLoginPage)}
                value='a'
              />
              <Checkbox
                name='forceotpbrowserflow'
                label='Force OTP browser flow'
                description='Enforces OTP-based MFA for Embrace Forms authentication'
                isChecked={tenantConfig?.idpForceOTPBrowserFlow}
                isDisabled={!isFieldEnabled('idpForceOTPBrowserFlow')}
                onClick={() => handleTenantConfigurationChange('idpForceOTPBrowserFlow', !tenantConfig?.idpForceOTPBrowserFlow)}
                value='a'
              />
            </Container>
            <Container>
              <Checkbox
                name='overridebrowserflow'
                label='Override browser flow'
                description='Use Embrace custom browser flow instead of the default, required for cases like migrating legacy credentials.'
                isChecked={tenantConfig?.idpOverrideBrowserFlow}
                isDisabled={!isFieldEnabled('idpOverrideBrowserFlow')}
                onClick={() => handleTenantConfigurationChange('idpOverrideBrowserFlow', !tenantConfig?.idpOverrideBrowserFlow)}
                value='a'
              />
            </Container>
            {/* Switch rather than Checkbox because this enables a client, not a flow variant, so
                the explanation goes in a Text description the way the housing groups do: Switch
                has no description prop. The redirect URL is spelled out rather than described
                because it is derived from a field in a different section, two cards down. */}
            <Container isVertical align='left'>
              <Text align='left' textStyle='h6' content='Umbraco' />
              <Text
                align='left'
                textStyle='description'
                textColor='grey'
                content={
                  umbracoRedirectUrl
                    ? `Keycloak: Clients → umbraco, on the tenant realm. Redirect URL ${umbracoRedirectUrl}, derived from Portal hostname under Customers Settings.`
                    : 'Keycloak: Clients → umbraco, on the tenant realm. Set Portal hostname under Customers Settings to give it a redirect URL.'
                }
              />
              {umbracoClientForcedOn && (
                <Text
                  align='left'
                  textStyle='description'
                  textColor='grey'
                  content='Always on for a tenant with customers; the switch only applies to runmode NoCustomers.'
                />
              )}
              <Container>
                <Switch
                  name='idpUmbracoClientEnabled'
                  label='Umbraco backoffice client'
                  onClick={() => handleTenantConfigurationChange('idpUmbracoClientEnabled', !tenantConfig?.idpUmbracoClientEnabled)}
                  isChecked={umbracoClientForcedOn || tenantConfig?.idpUmbracoClientEnabled}
                  isDisabled={umbracoClientForcedOn || !isFieldEnabled('idpUmbracoClientEnabled')}
                />
              </Container>
            </Container>
          </Container>
        </SectionCard>
        <SectionCard title='Additional Identity Providers'>
          <AdditionalOidcProviders
            value={tenantConfig?.idpAdditionalOidcProviders}
            onChange={(value) => handleTenantConfigurationChange('idpAdditionalOidcProviders', value)}
            isDisabled={!isFieldEnabled('idpAdditionalOidcProviders')}
          />
        </SectionCard>
        <SectionCard title='HelloId Settings'>
          <Container isVertical>
            <Container>
              <Switch
                name='helloIdEnabled'
                label='HelloId Enabled'
                value='a'
                onClick={() => handleTenantConfigurationChange('helloIdEnabled', !tenantConfig?.helloIdEnabled)}
                isChecked={tenantConfig?.helloIdEnabled}
                isDisabled={!isFieldEnabled('helloIdEnabled')}
              />
            </Container>

            {tenantConfig?.helloIdEnabled && (
              <>
                <Container>
                  <TextFieldWrapper labelText={'Client ID'}>
                    <TextField
                      isDisabled={!tenantConfig?.helloIdEnabled || !isFieldEnabled('helloIdClientId')}
                      value={tenantConfig?.helloIdClientId}
                      onChange={(e) => handleTenantConfigurationChange('helloIdClientId', e.target.value)}
                      type='text'
                      variant='default'
                    />
                  </TextFieldWrapper>
                  <SecretField
                    fieldName='helloIdClientSecret'
                    secretName='idp-helloid-clientsecret'
                    labelText='Client Secret'
                    value={tenantConfig?.helloIdClientSecret || ''}
                    onChange={handleTenantConfigurationChange}
                    isDisabled={!tenantConfig?.helloIdEnabled || !isFieldEnabled('helloIdClientSecret')}
                    tenantId={selectedTenantId}
                  />
                </Container>
                <Container>
                  <TextFieldWrapper labelText={'Tenant Id'}>
                    <TextField
                      isDisabled={!tenantConfig?.helloIdEnabled || !isFieldEnabled('helloIdTenantId')}
                      value={tenantConfig?.helloIdTenantId}
                      onChange={(e) => handleTenantConfigurationChange('helloIdTenantId', e.target.value)}
                      type='text'
                      variant='default'
                    />
                  </TextFieldWrapper>
                  <SecretField
                    fieldName='helloIdAzureAdClientSecret'
                    secretName='idp-helloid-azuread-clientsecret'
                    labelText='AzureAD Client Secret'
                    value={tenantConfig?.helloIdAzureAdClientSecret || ''}
                    onChange={handleTenantConfigurationChange}
                    isDisabled={!tenantConfig?.helloIdEnabled || !isFieldEnabled('helloIdAzureAdClientSecret')}
                    tenantId={selectedTenantId}
                  />
                </Container>
              </>
            )}
          </Container>
        </SectionCard>
        <SectionCard title='Housing Settings'>
          <Container isVertical>
            {/* Housing Keycloak, grouped the way Keycloak's own left-hand nav is, so a value set
                here can be followed click by click in the admin console. The master toggle is
                shown on every stage so the setting is discoverable, but only the environments in
                HOUSING_KEYCLOAK_ENVIRONMENTS can use it; the tooltip sits on the wrapper rather
                than the Switch because a disabled control does not reliably fire mouse events. */}
            <Container>
              <span
                data-tooltip-id='global-tooltip'
                data-tooltip-content={
                  housingKeycloakAvailable ? undefined : 'Housing Keycloak is only available for alpha, beta and production tenants for now.'
                }
              >
                <Switch
                  name='idpHousingKeycloakEnabled'
                  label='Housing Keycloak Enabled'
                  onClick={() => handleTenantConfigurationChange('idpHousingKeycloakEnabled', !tenantConfig?.idpHousingKeycloakEnabled)}
                  isChecked={housingKeycloakAvailable && tenantConfig?.idpHousingKeycloakEnabled}
                  isDisabled={!housingKeycloakAvailable || !isFieldEnabled('idpHousingKeycloakEnabled')}
                />
              </span>
            </Container>

            {housingKeycloakAvailable && tenantConfig?.idpHousingKeycloakEnabled && (
              <>
                {housingHostnameAvailable && (
                  <Container isVertical align='left'>
                    <Text align='left' textStyle='h6' content='Realm settings' />
                    <Text
                      align='left'
                      textStyle='description'
                      textColor='grey'
                      content={
                        'Keycloak: Realm settings \u2192 General \u2192 Frontend URL, set to https://<hostname>/auth. ' +
                        "Also adds https://<hostname>/* to the housing client's redirect URIs. Leave empty to let " +
                        'Keycloak derive both from the incoming request.'
                      }
                    />
                    <Container>
                      <TextFieldWrapper labelText={'Housing hostname'}>
                        <TextField
                          isDisabled={!isFieldEnabled('idpHousingKeycloakHostname')}
                          value={tenantConfig?.idpHousingKeycloakHostname}
                          onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakHostname', e.target.value)}
                          type='text'
                          variant='default'
                          placeholder='housing.example.nl'
                          errorMessage={
                            tenantConfig?.idpHousingKeycloakHostname && !HOSTNAME_PATTERN.test(tenantConfig.idpHousingKeycloakHostname)
                              ? 'Enter a bare hostname, without https:// or a path.'
                              : undefined
                          }
                        />
                      </TextFieldWrapper>
                    </Container>
                  </Container>
                )}

                {/* Its own group rather than a line in Realm settings, because in the admin
                    console this is a client and that is where an operator goes looking for it.
                    The ROPC caveat is stated outright: it is the reason upstream made this a
                    separate opt-in module instead of a flag on the housing client itself. */}
                <Container isVertical align='left'>
                  <Text align='left' textStyle='h6' content='Clients' />
                  <Text
                    align='left'
                    textStyle='description'
                    textColor='grey'
                    content={
                      'Keycloak: Clients → ci-test-client, on the housing realm. Adds a client for end to end ' +
                      'pipeline tests, with direct access grants (ROPC) permanently on. Leave off unless this ' +
                      'tenant actually runs those tests.'
                    }
                  />
                  <Container>
                    <Switch
                      name='idpHousingKeycloakCiTestClientEnabled'
                      label='CI test client'
                      onClick={() =>
                        handleTenantConfigurationChange(
                          'idpHousingKeycloakCiTestClientEnabled',
                          !tenantConfig?.idpHousingKeycloakCiTestClientEnabled
                        )
                      }
                      isChecked={tenantConfig?.idpHousingKeycloakCiTestClientEnabled}
                      isDisabled={!isFieldEnabled('idpHousingKeycloakCiTestClientEnabled')}
                    />
                  </Container>
                </Container>

                <Container isVertical align='left'>
                  <Text align='left' textStyle='h6' content='Identity providers' />
                  <Text
                    align='left'
                    textStyle='description'
                    textColor='grey'
                    content={'Keycloak: Identity providers \u2192 housing-employees-oidc, on the housing realm.'}
                  />
                  <Container>
                    <Switch
                      name='idpHousingKeycloakOidcEnabled'
                      label='Employees OIDC Enabled'
                      onClick={() => {
                        const enabling = !tenantConfig?.idpHousingKeycloakOidcEnabled
                        handleTenantConfigurationChange('idpHousingKeycloakOidcEnabled', enabling)
                        // Fill the conventional Webcenter identity host on the way on, never
                        // overwriting a value that is already there and never clearing one off.
                        if (enabling && !tenantConfig?.idpHousingKeycloakOidcBaseUrl) {
                          handleTenantConfigurationChange('idpHousingKeycloakOidcBaseUrl', webcenterHostnameFor(selectedTenant))
                        }
                      }}
                      isChecked={tenantConfig?.idpHousingKeycloakOidcEnabled}
                      isDisabled={!isFieldEnabled('idpHousingKeycloakOidcEnabled')}
                    />
                  </Container>

                  {tenantConfig?.idpHousingKeycloakOidcEnabled && (
                    <>
                      {/* Its own row: sharing one with the client id and secret left it a third of
                          the width, too narrow for the fully qualified host it is prefilled with. */}
                      <Container>
                        <TextFieldWrapper labelText={'Employees OIDC hostname'}>
                          <TextField
                            isDisabled={!isFieldEnabled('idpHousingKeycloakOidcBaseUrl')}
                            value={tenantConfig?.idpHousingKeycloakOidcBaseUrl}
                            onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakOidcBaseUrl', e.target.value)}
                            type='text'
                            variant='default'
                            placeholder={webcenterHostnameFor(selectedTenant)}
                            errorMessage={
                              tenantConfig?.idpHousingKeycloakOidcBaseUrl &&
                                !HOSTNAME_PATTERN.test(tenantConfig.idpHousingKeycloakOidcBaseUrl)
                                ? 'Enter a bare hostname, without https:// or a path.'
                                : undefined
                            }
                          />
                        </TextFieldWrapper>
                      </Container>
                      <Container>
                        <TextFieldWrapper labelText={'Employees OIDC client ID'}>
                          <TextField
                            isDisabled={!isFieldEnabled('idpHousingKeycloakOidcClientId')}
                            value={tenantConfig?.idpHousingKeycloakOidcClientId}
                            onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakOidcClientId', e.target.value)}
                            type='text'
                            variant='default'
                          />
                        </TextFieldWrapper>
                        <SecretField
                          fieldName='idpHousingKeycloakOidcClientSecret'
                          secretName='idp-housing-keycloak-oidc-clientsecret'
                          labelText='Employees OIDC client secret'
                          value={tenantConfig?.idpHousingKeycloakOidcClientSecret || ''}
                          onChange={handleTenantConfigurationChange}
                          isDisabled={!isFieldEnabled('idpHousingKeycloakOidcClientSecret')}
                          tenantId={selectedTenantId}
                        />
                      </Container>
                    </>
                  )}
                </Container>

                <Container isVertical align='left'>
                  <Text align='left' textStyle='h6' content='Adopt existing objects' />
                  <Text
                    align='left'
                    textStyle='description'
                    textColor='grey'
                    content={
                      'Imports a realm and its identity-provider and housing clients that already exist on the housing ' +
                      'Keycloak, instead of creating new ones. Everything else the package manages is still created, so a ' +
                      'realm that already holds those objects can fail with a conflict. Needs a full deploy: a suite update ' +
                      'skips the stage the imports run in.'
                    }
                  />
                  <Container>
                    <Switch
                      name='idpHousingKeycloakImportEnabled'
                      label='Import Existing Realm And Clients'
                      onClick={() =>
                        handleTenantConfigurationChange('idpHousingKeycloakImportEnabled', !tenantConfig?.idpHousingKeycloakImportEnabled)
                      }
                      isChecked={tenantConfig?.idpHousingKeycloakImportEnabled}
                      isDisabled={!isFieldEnabled('idpHousingKeycloakImportEnabled')}
                    />
                  </Container>

                  {tenantConfig?.idpHousingKeycloakImportEnabled && (
                    <>
                      <Container>
                        <TextFieldWrapper labelText={'Existing realm name'}>
                          <TextField
                            isDisabled={!isFieldEnabled('idpHousingKeycloakImportRealm')}
                            value={tenantConfig?.idpHousingKeycloakImportRealm}
                            onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakImportRealm', e.target.value)}
                            type='text'
                            variant='default'
                            placeholder='Leave empty to create a new realm'
                          />
                        </TextFieldWrapper>
                      </Container>
                      <Container>
                        <TextFieldWrapper labelText={'Existing identity-provider client UUID'}>
                          <TextField
                            isDisabled={!isFieldEnabled('idpHousingKeycloakImportIdentityClientId')}
                            value={tenantConfig?.idpHousingKeycloakImportIdentityClientId}
                            onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakImportIdentityClientId', e.target.value)}
                            type='text'
                            variant='default'
                            placeholder='00000000-0000-0000-0000-000000000000'
                            errorMessage={
                              tenantConfig?.idpHousingKeycloakImportIdentityClientId &&
                                !UUID_PATTERN.test(tenantConfig.idpHousingKeycloakImportIdentityClientId)
                                ? "Keycloak's internal client UUID, from the client's URL in the admin console, not the clientId."
                                : undefined
                            }
                          />
                        </TextFieldWrapper>
                        <TextFieldWrapper labelText={'Existing housing client UUID'}>
                          <TextField
                            isDisabled={!isFieldEnabled('idpHousingKeycloakImportHousingClientId')}
                            value={tenantConfig?.idpHousingKeycloakImportHousingClientId}
                            onChange={(e) => handleTenantConfigurationChange('idpHousingKeycloakImportHousingClientId', e.target.value)}
                            type='text'
                            variant='default'
                            placeholder='00000000-0000-0000-0000-000000000000'
                            errorMessage={
                              tenantConfig?.idpHousingKeycloakImportHousingClientId &&
                                !UUID_PATTERN.test(tenantConfig.idpHousingKeycloakImportHousingClientId)
                                ? "Keycloak's internal client UUID, from the client's URL in the admin console, not the clientId."
                                : undefined
                            }
                          />
                        </TextFieldWrapper>
                      </Container>
                      <Container isVertical>
                        <Switch
                          name='idpHousingKeycloakImportPortalClient'
                          label='Import Existing Portal Client'
                          onClick={() =>
                            handleTenantConfigurationChange(
                              'idpHousingKeycloakImportPortalClient',
                              !tenantConfig?.idpHousingKeycloakImportPortalClient
                            )
                          }
                          isChecked={tenantConfig?.idpHousingKeycloakImportPortalClient}
                          isDisabled={!isFieldEnabled('idpHousingKeycloakImportPortalClient')}
                        />
                        <Text textStyle='description'>
                          For a realm that already has a portal client copied in: also imports it with its mappers, the Embrace browser flow
                          and the portal-user and userinfo-roles scopes, all found by name. Leave off for a realm without a portal client.
                        </Text>
                      </Container>
                    </>
                  )}
                </Container>
              </>
            )}
          </Container>
        </SectionCard>
        <SectionCard title='Customers Settings'>
          <Container isVertical>
            <Container>
              <TextFieldWrapper labelText={'Backend hostname'} style={{ flex: 2 }}>
                <TextField
                  isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('gateway1CustomerHostname')}
                  value={tenantConfig?.gateway1CustomerHostname}
                  onChange={(e) => handleTenantConfigurationChange('gateway1CustomerHostname', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText={'Port'} style={{ flex: 1 }}>
                <Autocomplete
                  placeholder={'Select a value...'}
                  value={tenantConfig?.customersBackendPortForward}
                  isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersBackendPortForward')}
                  onChange={(e) => handleTenantConfigurationChange('customersBackendPortForward', e.value)}
                  options={[
                    { label: '443', value: '443' },
                    { label: '444', value: '444' },
                    { label: '445', value: '445' },
                    { label: '446', value: '446' },
                    { label: '447', value: '447' }
                  ]}
                />
              </TextFieldWrapper>
              <TextFieldWrapper labelText='Connectivity' style={{ flex: 1 }}>
                <ConnectivityChecker hostname={tenantConfig?.gateway1CustomerHostname} port={tenantConfig?.customersBackendPortForward} />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'UI hostname'}>
                <TextField
                  isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersUIHostname')}
                  value={tenantConfig?.customersUIHostname}
                  onChange={(e) => handleTenantConfigurationChange('customersUIHostname', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>

              <TextFieldWrapper labelText={'FE version'}>
                <TextField
                  isDisabled={!isFieldEnabled('suiteVersion')}
                  value={tenantConfig?.suiteVersion}
                  onChange={(e) => handleTenantConfigurationChange('suiteVersion', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>

            <Container>
              <TextFieldWrapper labelText={'Portal hostname'}>
                <TextField
                  value={tenantConfig?.fePortalUrl}
                  isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('fePortalUrl')}
                  onChange={(e) => handleTenantConfigurationChange('fePortalUrl', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>

              <TextFieldWrapper labelText={'API key'}>
                <TextField
                  isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('gateway1CustomerAplicationId')}
                  value={tenantConfig?.gateway1CustomerAplicationId}
                  onChange={(e) => handleTenantConfigurationChange('gateway1CustomerAplicationId', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
          </Container>
          <Container isVertical>
            <Container>
              <Switch
                isDisabled={!isFieldEnabled('idpCustomersPortalsEnabled')}
                name='customerportaloidcenabled'
                label='Customers Portal OIDC'
                value='a'
                onClick={() => handleTenantConfigurationChange('idpCustomersPortalsEnabled', !tenantConfig?.idpCustomersPortalsEnabled)}
                isChecked={tenantConfig?.idpCustomersPortalsEnabled}
              />
            </Container>

            {tenantConfig?.idpCustomersPortalsEnabled && (
              <>
                <Container>
                  <TextFieldWrapper labelText={'Idp portal hostname'}>
                    <TextField
                      isDisabled={!tenantConfig?.idpCustomersPortalsEnabled || !isFieldEnabled('idpCustomersPortalsHostname')}
                      value={tenantConfig?.idpCustomersPortalsHostname}
                      onChange={(e) => handleTenantConfigurationChange('idpCustomersPortalsHostname', e.target.value)}
                      type='text'
                      variant='default'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Client ID'}>
                    <TextField
                      isDisabled={!tenantConfig?.idpCustomersPortalsEnabled || !isFieldEnabled('idpCustomersPortalsClientId')}
                      value={tenantConfig?.idpCustomersPortalsClientId}
                      onChange={(e) => handleTenantConfigurationChange('idpCustomersPortalsClientId', e.target.value)}
                      type='text'
                      variant='default'
                    />
                  </TextFieldWrapper>
                </Container>
                <Container>
                  <TextFieldWrapper labelText={'Client Secret'}>
                    <TextField
                      isDisabled={!tenantConfig?.idpCustomersPortalsEnabled || !isFieldEnabled('idpCustomersPortalsClientSecret')}
                      value={tenantConfig?.idpCustomersPortalsClientSecret}
                      onChange={(e) => handleTenantConfigurationChange('idpCustomersPortalsClientSecret', e.target.value)}
                      type='password'
                      variant='default'
                    />
                  </TextFieldWrapper>
                </Container>
              </>
            )}
          </Container>
        </SectionCard>
        <SectionCard title='Shared Payments Settings'>
          <Container isVertical>
            <Container>
              <TextFieldWrapper labelText={'Shared Payments Providers'}>
                <Autocomplete
                  value={tenantConfig?.sharedPaymentsProvider}
                  isDisabled={!isFieldEnabled('sharedPaymentsProvider')}
                  onChange={(e) => handleTenantConfigurationChange('sharedPaymentsProvider', e.value)}
                  options={[
                    { label: 'None', value: 'None' },
                    { label: 'Ogone', value: 'Ogone' },
                    { label: 'MultiSafePay', value: 'MultiSafePay' },
                    { label: 'Mail2Pay', value: 'Mail2Pay' },
                    { label: 'Pay', value: 'Pay' },
                    { label: 'Emulator', value: 'Emulator' },
                    { label: 'WorldlineDirect', value: 'WorldlineDirect' }
                  ]}
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'config'}>
                <TextField
                  isDisabled={!isFieldEnabled('sharedPaymentsProviderConfiguration')}
                  value={tenantConfig?.sharedPaymentsProviderConfiguration}
                  onChange={(e) => handleTenantConfigurationChange('sharedPaymentsProviderConfiguration', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
          </Container>
        </SectionCard>
        <SectionCard title='Social Settings'>
          <Container isVertical>
            <Container>
              <Switch
                name='socialcontentmigrationenabled'
                label='Social Content Migration'
                value='a'
                isDisabled={!isFieldEnabled('socialContentMigrationEnabled')}
                onClick={() =>
                  handleTenantConfigurationChange('socialContentMigrationEnabled', !tenantConfig?.socialContentMigrationEnabled)
                }
                isChecked={tenantConfig?.socialContentMigrationEnabled}
              />
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Liquit url (csp setting only)'}>
                <TextField
                  value={tenantConfig?.liquitUrl}
                  isDisabled={!isFieldEnabled('liquitUrl')}
                  onChange={(e) => handleTenantConfigurationChange('liquitUrl', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
            <Container>
              <TextFieldWrapper labelText={'Custom scopes (space-separated)'}>
                <TextField
                  value={tenantConfig?.customScopes}
                  isDisabled={!isFieldEnabled('customScopes')}
                  onChange={(e) => handleTenantConfigurationChange('customScopes', e.target.value)}
                  type='text'
                  variant='default'
                />
              </TextFieldWrapper>
            </Container>
          </Container>
        </SectionCard>
        <SectionCard title='Analytics'>
          <Container>
            <TextFieldWrapper labelText={'Matomo internal hostname'} style={{ flex: 2 }}>
              <TextField
                value={tenantConfig?.matomoInteralHostname}
                isDisabled={!isFieldEnabled('matomoInteralHostname')}
                onChange={(e) => handleTenantConfigurationChange('matomoInteralHostname', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            <TextFieldWrapper labelText={'Site id'} style={{ flex: 1 }}>
              <TextField
                value={tenantConfig?.matomoInteralSiteId}
                isDisabled={!isFieldEnabled('matomoInteralSiteId')}
                onChange={(e) => handleTenantConfigurationChange('matomoInteralSiteId', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            <TextFieldWrapper labelText={'Container id'} style={{ flex: 1 }}>
              <TextField
                value={tenantConfig?.matomoInternalContainerId}
                isDisabled={!isFieldEnabled('matomoInternalContainerId')}
                onChange={(e) => handleTenantConfigurationChange('matomoInternalContainerId', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
          </Container>
          <Container>
            <TextFieldWrapper labelText={'Log Rocket API key'} style={{ flex: 1 }}>
              <TextField
                value={tenantConfig?.logRocketApiKey}
                isDisabled={!isFieldEnabled('logRocketApiKey')}
                onChange={(e) => handleTenantConfigurationChange('logRocketApiKey', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
            <TextFieldWrapper labelText={'Google Analytics Measurement ID'} style={{ flex: 1 }}>
              <TextField
                value={tenantConfig?.googleAnalyticsId}
                isDisabled={!isFieldEnabled('googleAnalyticsId')}
                onChange={(e) => handleTenantConfigurationChange('googleAnalyticsId', e.target.value)}
                type='text'
                variant='default'
              />
            </TextFieldWrapper>
          </Container>
        </SectionCard>
        <SectionCard title='Feature Flags (Until App Config)'>
          <Container>
            <Container align='left' isVertical>
              <Switch
                name='customers-mail-module'
                label='Customers mail module'
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersMailEnabled')}
                onClick={() => handleTenantConfigurationChange('customersMailEnabled', !tenantConfig?.customersMailEnabled)}
                isChecked={tenantConfig?.customersMailEnabled}
              />
              <Switch
                name='customers-chat-module'
                label='Customers chat module'
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersChatEnabled')}
                onClick={() => handleTenantConfigurationChange('customersChatEnabled', !tenantConfig?.customersChatEnabled)}
                isChecked={tenantConfig?.customersChatEnabled}
              />
              <Switch
                name='customers-telephony-module'
                label='Customers telephony module'
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersTelephonyEnabled')}
                onClick={() => handleTenantConfigurationChange('customersTelephonyEnabled', !tenantConfig?.customersTelephonyEnabled)}
                isChecked={tenantConfig?.customersTelephonyEnabled}
              />
              <Switch
                name='customers-tasks-module'
                label='Customers tasks module'
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersTasksEnabled')}
                onClick={() => handleTenantConfigurationChange('customersTasksEnabled', !tenantConfig?.customersTasksEnabled)}
                isChecked={tenantConfig?.customersTasksEnabled}
              />
              <span data-tooltip-id='global-tooltip' data-tooltip-content={`Sets the GraphQL gateway hostname to ${hiveGateway2Hostname}`}>
                <Switch
                  name='use-hive-gateway-2-gql'
                  label='Use Hive-gateway 2 (GQL)'
                  value='a'
                  isDisabled={isHiveGatewayAlwaysOn || !isFieldEnabled('useHiveGateway')}
                  onClick={() => handleUseHiveGatewayChange(!isHiveGatewayOn)}
                  isChecked={isHiveGatewayOn}
                />
              </span>

              {/* Permanently on and non-toggleable on purpose: shown so it's visible, not because it's an active control. */}
              <span
                data-tooltip-id='global-tooltip'
                data-tooltip-content={`Sets the subscriptions (WS) gateway hostname to ${hiveGateway2Hostname}`}
              >
                <Switch
                  name='use-hive-gateway-2-ws'
                  label='Use Hive-gateway 2 (WS)'
                  value='a'
                  isDisabled
                  onClick={() => { }}
                  isChecked
                />
              </span>
            </Container>
            <Container align='left' isVertical>
              <Switch
                name='customers-caseflow-module'
                label='Customers caseflow module'
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersCaseflowEnabled')}
                onClick={() => handleTenantConfigurationChange('customersCaseflowEnabled', !tenantConfig?.customersCaseflowEnabled)}
                isChecked={tenantConfig?.customersCaseflowEnabled}
              />
              <Switch
                name='customers-dossiers-module'
                label='Customers dossiers module '
                value='a'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersDossiersEnabled')}
                onClick={() => handleTenantConfigurationChange('customersDossiersEnabled', !tenantConfig?.customersDossiersEnabled)}
                isChecked={tenantConfig?.customersDossiersEnabled}
              />
              <Switch
                name='customers-disable-chat-notifications'
                label='Customers disable chat notificatons'
                isDisabled={tenantConfig?.feRunMode === 'NoCustomers' || !isFieldEnabled('customersDisableChatNotifications')}
                onClick={() =>
                  handleTenantConfigurationChange('customersDisableChatNotifications', !tenantConfig?.customersDisableChatNotifications)
                }
                value='a'
                isChecked={tenantConfig?.customersDisableChatNotifications}
              />
              <Switch
                name='public-portal-theming'
                label='Public portal theming'
                isDisabled={!isFieldEnabled('publicPortalThemingEnabled')}
                onClick={() => handleTenantConfigurationChange('publicPortalThemingEnabled', !tenantConfig?.publicPortalThemingEnabled)}
                value='a'
                isChecked={tenantConfig?.publicPortalThemingEnabled}
              />
              {/* The tooltip sits on the wrapper rather than the Switch because a disabled control
                  does not reliably fire mouse events. */}
              <span data-tooltip-id='global-tooltip' data-tooltip-content={`Sets the GraphQL gateway hostname to ${gatewayApiHostname}`}>
                <Switch
                  name='use-gateway-api-gql'
                  label='Use Gateway API (GQL)'
                  value='a'
                  isDisabled={!isFieldEnabled('enableGatewayApi')}
                  onClick={() => handleUseGatewayApiChange(!tenantConfig?.enableGatewayApi)}
                  isChecked={isHiveGatewayOn && (tenantConfig?.enableGatewayApi ?? false)}
                />
              </span>
              {/* Permanently on and non-toggleable on purpose: every tenant's WS goes through the Gateway API. */}
              <span
                data-tooltip-id='global-tooltip'
                data-tooltip-content={`Sets the subscriptions (WS) gateway hostname to ${gatewayApiHostname}`}
              >
                <Switch name='use-gateway-api-ws' label='Use Gateway API (WS)' value='a' isDisabled onClick={() => {}} isChecked />
              </span>
            </Container>
          </Container>
        </SectionCard>
        <Accordion id='cms-settings' headerTitle='CMS Configuration' isFullWidth={true}>
          <Container isVertical>
            <Container align='left' isVertical>
              <Checkbox
                isDisabled={tenantConfig?.feRunMode !== 'CustomersOnly' || !isFieldEnabled('enableCMSPackages')}
                name='ts1'
                label='CMS Packages'
                description='Default enabled for runmodes FullSuite and NoCustomers'
                onClick={() => handleTenantConfigurationChange('enableCMSPackages', !tenantConfig?.enableCMSPackages)}
                value='a'
                isChecked={tenantConfig?.enableCMSPackages || tenantConfig?.feRunMode !== 'CustomersOnly'}
              />
              <TextFieldWrapper labelText={'Comma separated widget packs that are used by the tenant'}>
                <TextField
                  value={tenantConfig?.portalWidgetPacks}
                  onChange={(e) => handleTenantConfigurationChange('portalWidgetPacks', e.target.value)}
                  type='text'
                  variant='default'
                  isDisabled={!isFieldEnabled('portalWidgetPacks')}
                />
              </TextFieldWrapper>
            </Container>
          </Container>
        </Accordion>
        <Divider
          size='100%'
          spacing={{
            bottom: 1,
            top: 1
          }}
        ></Divider>
        <Accordion id='portal-ui-settings' headerTitle='Portal UI Configuration' isFullWidth={true}>
          <Container isVertical>
            <Container>
              <Checkbox
                name='enable-portalui-packages'
                label='Enable Portal UI Module'
                description='Enable public-facing portal functionality for this tenant'
                onClick={() => handleTenantConfigurationChange('enablePortalUIPackages', !tenantConfig?.enablePortalUIPackages)}
                value='a'
                isChecked={tenantConfig?.enablePortalUIPackages}
                isDisabled={!isFieldEnabled('enablePortalUIPackages')}
              />
            </Container>

            {tenantConfig?.enablePortalUIPackages && (
              <>
                <Divider
                  size='100%'
                  spacing={{
                    bottom: 10,
                    top: 10
                  }}
                />
                <Container>
                  <Text textStyle='h6'>General Settings</Text>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Portal ID (base64 encoded)'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIPortalId || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalId', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalId')}
                      placeholder='Required: Unique portal identifier'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Portal Name'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIPortalName || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalName', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalName')}
                      placeholder='Required: Used in URL generation'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Custom Hostname'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIHostname || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIHostname', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIHostname')}
                      placeholder='Optional: Auto-generated if empty'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Identity Provider Hint'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIPortalIdpHint || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalIdpHint', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalIdpHint')}
                      placeholder='Optional: IDP hint for authentication'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Release Version'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIPortalReleaseVersion || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalReleaseVersion', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalReleaseVersion')}
                      placeholder='Optional: e.g., r2000'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Portal Client'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIPortalClient || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalClient', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalClient')}
                      placeholder='Default: portal'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'OpenStreetMap Tile Server URL'}>
                    <TextField
                      value={tenantConfig?.portalUIPortalOsmUrl || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalOsmUrl', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalOsmUrl')}
                      placeholder='Default: https://tileserver.embracecloud.nl/styles/maptiler-basic/{z}/{x}/{y}.png'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Street View URL'}>
                    <TextField
                      value={tenantConfig?.portalUIPortalStreetviewUrl || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIPortalStreetviewUrl', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIPortalStreetviewUrl')}
                      placeholder='Default: https://www.google.com/maps/@?api=1&map_action=pano&viewpoint={{latitude}},{{longitude}}'
                    />
                  </TextFieldWrapper>
                </Container>

                <Divider
                  size='100%'
                  spacing={{
                    bottom: 10,
                    top: 10
                  }}
                />
                <Container>
                  <Text textStyle='h6'>Media Handler Settings</Text>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Max File Size (bytes)'}>
                    <TextField
                      value={tenantConfig?.portalUIMediaHandlerMaxFileSize?.toString() || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIMediaHandlerMaxFileSize', parseInt(e.target.value) || 0)}
                      type='number'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIMediaHandlerMaxFileSize')}
                      placeholder='Default: 8388608 (8MB)'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Accepted File Types'} style={{ flex: 1 }}>
                    <Autocomplete
                      isSearchable={false}
                      isMulti={true}
                      options={[
                        { label: '.doc', value: '.doc' },
                        { label: '.docx', value: '.docx' },
                        { label: '.jpg', value: '.jpg' },
                        { label: '.png', value: '.png' },
                        { label: '.pdf', value: '.pdf' },
                        { label: '.jpeg', value: '.jpeg' },
                        { label: '.webp', value: '.webp' },
                        { label: '.gif', value: '.gif' },
                        { label: '.bmp', value: '.bmp' },
                        { label: '.svg', value: '.svg' },
                        { label: '.xls', value: '.xls' },
                        { label: '.xlsx', value: '.xlsx' },
                        { label: '.txt', value: '.txt' },
                        { label: '.zip', value: '.zip' },
                        { label: '.rar', value: '.rar' }
                      ]}
                      value={selectedPortalUIMediaHandlerFileTypes}
                      onChange={(selected: any) => {
                        setSelectedPortalUIMediaHandlerFileTypes(selected)
                        const newValue = Array.isArray(selected) ? selected.map((s: any) => s.value).join(',') : ''
                        handleTenantConfigurationChange('portalUIMediaHandlerAcceptedFileTypes', newValue)
                      }}
                      placeholder='Default: .doc,.docx,.jpg,.png,.pdf,.jpeg,.webp'
                      isDisabled={!isFieldEnabled('portalUIMediaHandlerAcceptedFileTypes')}
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Attachment Type ID'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIMediaHandlerAttachmentTypeId || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIMediaHandlerAttachmentTypeId', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIMediaHandlerAttachmentTypeId')}
                      placeholder='Optional: Attachment type identifier'
                    />
                  </TextFieldWrapper>
                </Container>

                <Divider
                  size='100%'
                  spacing={{
                    bottom: 10,
                    top: 10
                  }}
                />
                <Container>
                  <Text textStyle='h6'>Additional Settings</Text>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Custom Gateway'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUICustomGateway || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUICustomGateway', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUICustomGateway')}
                      placeholder='Optional: Custom gateway hostname'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Extra Hostnames (comma-separated)'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIExtraHostnames || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIExtraHostnames', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIExtraHostnames')}
                      placeholder='Optional: Additional domain names'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Custom Authority'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUICustomAuthority || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUICustomAuthority', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUICustomAuthority')}
                      placeholder='Optional: Custom authority hostname'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Umami Website ID'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIUmamiWebsiteId || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIUmamiWebsiteId', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIUmamiWebsiteId')}
                      placeholder='Optional: visitor analytics, needs the script URL too'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Umami Script URL'} style={{ flex: 1 }}>
                    <TextField
                      value={tenantConfig?.portalUIUmamiScriptUrl || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUIUmamiScriptUrl', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUIUmamiScriptUrl')}
                      placeholder='Optional: https://analytics.<environment>.embracecloud.io/insight.js'
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <TextFieldWrapper labelText={'Tolkie token'} style={{ flex: 2 }}>
                    <TextField
                      value={tenantConfig?.portalUITolkieToken || ''}
                      onChange={(e) => handleTenantConfigurationChange('portalUITolkieToken', e.target.value)}
                      type='text'
                      variant='default'
                      isDisabled={!isFieldEnabled('portalUITolkieToken')}
                      placeholder='Optional: Tolkie reading aid, empty is off'
                    />
                  </TextFieldWrapper>
                  <TextFieldWrapper labelText={'Tolkie buttons'} style={{ flex: 1 }}>
                    <Autocomplete
                      value={tenantConfig?.portalUITolkieState || 'separateButtons'}
                      isDisabled={!isFieldEnabled('portalUITolkieState')}
                      onChange={(e) => handleTenantConfigurationChange('portalUITolkieState', e.value)}
                      options={[
                        { label: 'Separate buttons', value: 'separateButtons' },
                        { label: 'Floating button', value: 'floatingButton' }
                      ]}
                    />
                  </TextFieldWrapper>
                </Container>

                <Container>
                  <Checkbox
                    name='portalui-enable-www'
                    label='Enable WWW Subdomain'
                    description='Enable www subdomain redirect'
                    onClick={() => handleTenantConfigurationChange('portalUIEnableWww', !tenantConfig?.portalUIEnableWww)}
                    value='a'
                    isChecked={tenantConfig?.portalUIEnableWww}
                    isDisabled={!isFieldEnabled('portalUIEnableWww')}
                  />
                </Container>
              </>
            )}
          </Container>
        </Accordion>
        <Divider
          size='100%'
          spacing={{
            bottom: 1,
            top: 1
          }}
        ></Divider>
        <Accordion id='translation-provider-settings' headerTitle='Translation Provider' isFullWidth={true}>
          <Container isVertical>
            <Container align='left' isVertical>
              <Checkbox
                name='translationprovider-widgets'
                label='Show Widgets translations'
                description='Controls visibility only: which groups the Translation Manager offers as filters. It does not grant or restrict access.'
                onClick={() => handleTenantConfigurationChange('translationProviderWidgets', !tenantConfig?.translationProviderWidgets)}
                value='a'
                isChecked={tenantConfig?.translationProviderWidgets}
                isDisabled={!isFieldEnabled('translationProviderWidgets')}
              />
              <Checkbox
                name='translationprovider-suite'
                label='Show Suite translations'
                description='Turning both off is treated as unconfigured, and the Translation Manager shows both groups.'
                onClick={() => handleTenantConfigurationChange('translationProviderSuite', !tenantConfig?.translationProviderSuite)}
                value='a'
                isChecked={tenantConfig?.translationProviderSuite}
                isDisabled={!isFieldEnabled('translationProviderSuite')}
              />
            </Container>
          </Container>
        </Accordion>
      </div>
      <div
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 5,
          background: 'var(--tma-panel-bg)',
          borderTop: '1px solid var(--tma-card-border)',
          padding: '12px 0',
          marginTop: '4px'
        }}
      >
        {renderUpdateButtons()}
        {renderPlanAndSourceRow()}
      </div>
    </>
  )
}

export default TenantDeploymentSettings
